import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, CheckCircle, Clock, Home, ListTodo,
  Users, MessageCircle, Bell, Menu, X, ChevronRight,
  Droplets, Wifi, Zap, Phone, Plus, Check, Info,
  Sun, Moon, Coffee, Waves, DollarSign, Package
} from 'lucide-react';

// Mobile-first pousada operations
// Everything works with your thumb while walking around

interface Task {
  id: string;
  text: string;
  priority: 'critical' | 'high' | 'normal';
  location?: string;
  time?: string;
  done?: boolean;
}

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  message: string;
  location?: string;
}

const PousadaMobile: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'now' | 'tasks' | 'alerts' | 'status'>('now');
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Current critical items that need attention NOW
  const nowItems = [
    { id: '1', type: 'alert', icon: Droplets, color: 'text-red-500', title: 'Water Tank Low', detail: 'Block B - 35%', action: 'Check pump' },
    { id: '2', type: 'arrival', icon: Home, color: 'text-green-500', title: 'Silva Family Arriving', detail: '14:30 - Room 12', action: 'Prep welcome' },
    { id: '3', type: 'task', icon: Coffee, color: 'text-orange-500', title: 'Breakfast Running Low', detail: 'Fruit, bread, eggs', action: 'Call supplier' },
    { id: '4', type: 'checkout', icon: Users, color: 'text-blue-500', title: '3 Checkouts', detail: 'Rooms 3, 7, 9', action: 'Process payment' }
  ];

  const tasks: Task[] = [
    { id: '1', text: 'Check pool chemicals', priority: 'critical', location: 'Pool', time: '08:00' },
    { id: '2', text: 'Room 7 deep clean', priority: 'high', location: 'Block A', time: '10:00' },
    { id: '3', text: 'Restock mini-bars', priority: 'normal', location: 'All rooms', time: '14:00' },
    { id: '4', text: 'Beach umbrellas setup', priority: 'normal', location: 'Beach', time: '07:00', done: true },
    { id: '5', text: 'Wifi router reset', priority: 'high', location: 'Reception', time: 'ASAP' },
  ];

  const alerts: Alert[] = [
    { id: '1', type: 'critical', message: 'Generator needs fuel', location: 'Utility room' },
    { id: '2', type: 'warning', message: 'Tomorrow: 8 arrivals', location: 'Reception' },
    { id: '3', type: 'info', message: 'Massage therapist confirmed 16:00', location: 'Room 5' }
  ];

  const statusItems = [
    { label: 'Occupancy', value: '9/12', percent: 75, color: 'bg-green-500' },
    { label: 'Water', value: '65%', percent: 65, color: 'bg-blue-500' },
    { label: 'Staff', value: '6/7', percent: 86, color: 'bg-purple-500' },
    { label: 'Tasks', value: '12/18', percent: 67, color: 'bg-orange-500' }
  ];

  const quickActions = [
    { icon: Phone, label: 'Call', action: () => {} },
    { icon: MessageCircle, label: 'WhatsApp', action: () => {} },
    { icon: Package, label: 'Supplies', action: () => {} },
    { icon: DollarSign, label: 'Payment', action: () => {} }
  ];

  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      default: return 'bg-blue-500';
    }
  };

  const getAlertColor = (type: string) => {
    switch(type) {
      case 'critical': return 'bg-red-500/20 border-red-500 text-red-500';
      case 'warning': return 'bg-yellow-500/20 border-yellow-500 text-yellow-500';
      default: return 'bg-blue-500/20 border-blue-500 text-blue-500';
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header - Always visible */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div>
              <div className="text-lg font-semibold">Pousada Ops</div>
              <div className="text-xs text-gray-400">
                {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
          
          {/* Quick Status Icons */}
          <div className="flex items-center gap-3">
            <Wifi className="w-5 h-5 text-green-400" />
            <Zap className="w-5 h-5 text-green-400" />
            <Droplets className="w-5 h-5 text-yellow-400" />
            <Bell className="w-5 h-5 text-red-400" />
          </div>
        </div>
      </header>

      {/* Side Menu - Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setMenuOpen(false)}
            />
            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              className="fixed left-0 top-0 h-full w-72 bg-gray-800 z-50 p-6 overflow-y-auto"
            >
              <h2 className="text-xl font-bold mb-6">Quick Actions</h2>
              <div className="space-y-2">
                {quickActions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      action.action();
                      setMenuOpen(false);
                    }}
                    className="w-full p-4 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center gap-4 transition-colors"
                  >
                    <action.icon className="w-5 h-5 text-blue-400" />
                    <span>{action.label}</span>
                    <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                  </button>
                ))}
              </div>
              
              <h3 className="text-sm font-semibold text-gray-400 uppercase mt-8 mb-3">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Today's Revenue</span>
                  <span className="text-green-400 font-medium">R$ 3,450</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Occupancy</span>
                  <span className="font-medium">75%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Staff Present</span>
                  <span className="font-medium">6/7</span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto pb-20">
        {/* NOW Tab - What needs attention immediately */}
        {activeTab === 'now' && (
          <div className="p-4 space-y-3">
            <h2 className="text-sm font-semibold text-gray-400 uppercase mb-3">
              Needs Attention Now
            </h2>
            {nowItems.map(item => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-lg p-4"
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg bg-gray-700`}>
                    <item.icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">{item.title}</h3>
                    <p className="text-sm text-gray-400">{item.detail}</p>
                    <button className="mt-2 text-sm text-blue-400 hover:text-blue-300">
                      → {item.action}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
            
            {/* Add New Quick Task */}
            <button className="w-full p-4 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center gap-2 text-gray-400">
              <Plus className="w-5 h-5" />
              Add Quick Task
            </button>
          </div>
        )}

        {/* TASKS Tab */}
        {activeTab === 'tasks' && (
          <div className="p-4">
            {/* Shift Pills */}
            <div className="flex gap-2 mb-4 overflow-x-auto">
              <button className="px-4 py-2 bg-blue-600 rounded-lg flex items-center gap-2 whitespace-nowrap">
                <Sun className="w-4 h-4" />
                Morning
              </button>
              <button className="px-4 py-2 bg-gray-700 rounded-lg flex items-center gap-2 whitespace-nowrap">
                <Sun className="w-4 h-4" />
                Afternoon
              </button>
              <button className="px-4 py-2 bg-gray-700 rounded-lg flex items-center gap-2 whitespace-nowrap">
                <Moon className="w-4 h-4" />
                Evening
              </button>
            </div>

            {/* Task List */}
            <div className="space-y-2">
              {tasks.map(task => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`bg-gray-800 rounded-lg p-4 ${task.done ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        task.done 
                          ? 'bg-green-500 border-green-500' 
                          : 'border-gray-600'
                      }`}
                    >
                      {task.done && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                        <span className="font-medium">{task.text}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        {task.time && <span>{task.time}</span>}
                        {task.location && (
                          <>
                            <span>•</span>
                            <span>{task.location}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* ALERTS Tab */}
        {activeTab === 'alerts' && (
          <div className="p-4 space-y-3">
            {alerts.map(alert => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`border rounded-lg p-4 ${getAlertColor(alert.type)}`}
              >
                <div className="flex items-start gap-3">
                  {alert.type === 'critical' && <AlertTriangle className="w-5 h-5 mt-0.5" />}
                  {alert.type === 'warning' && <Clock className="w-5 h-5 mt-0.5" />}
                  {alert.type === 'info' && <Info className="w-5 h-5 mt-0.5" />}
                  <div className="flex-1">
                    <p className="font-medium">{alert.message}</p>
                    {alert.location && (
                      <p className="text-sm mt-1 opacity-75">{alert.location}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* STATUS Tab */}
        {activeTab === 'status' && (
          <div className="p-4">
            {/* Quick Metrics */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {statusItems.map((item, i) => (
                <div key={i} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">{item.label}</span>
                    <span className="font-semibold">{item.value}</span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${item.color} transition-all duration-500`}
                      style={{ width: `${item.percent}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Live Feed */}
            <h3 className="text-sm font-semibold text-gray-400 uppercase mb-3">Live Updates</h3>
            <div className="space-y-2">
              <div className="bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <div className="flex-1">
                    <p className="text-sm">Room 7 cleaned</p>
                    <p className="text-xs text-gray-500">2 min ago</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-blue-400" />
                  <div className="flex-1">
                    <p className="text-sm">Guest checked in - Room 12</p>
                    <p className="text-xs text-gray-500">15 min ago</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-800 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <Coffee className="w-4 h-4 text-orange-400" />
                  <div className="flex-1">
                    <p className="text-sm">Breakfast service started</p>
                    <p className="text-xs text-gray-500">1 hour ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Navigation - Always visible */}
      <nav className="bg-gray-800 border-t border-gray-700 px-4 py-2 fixed bottom-0 left-0 right-0 z-30">
        <div className="flex items-center justify-around">
          <button
            onClick={() => setActiveTab('now')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'now' 
                ? 'text-blue-400 bg-gray-700' 
                : 'text-gray-400'
            }`}
          >
            <Bell className="w-5 h-5" />
            <span className="text-xs">Now</span>
          </button>
          
          <button
            onClick={() => setActiveTab('tasks')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'tasks' 
                ? 'text-blue-400 bg-gray-700' 
                : 'text-gray-400'
            }`}
          >
            <ListTodo className="w-5 h-5" />
            <span className="text-xs">Tasks</span>
          </button>
          
          <button
            onClick={() => setActiveTab('alerts')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors relative ${
              activeTab === 'alerts' 
                ? 'text-blue-400 bg-gray-700' 
                : 'text-gray-400'
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
            <span className="text-xs">Alerts</span>
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          </button>
          
          <button
            onClick={() => setActiveTab('status')}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'status' 
                ? 'text-blue-400 bg-gray-700' 
                : 'text-gray-400'
            }`}
          >
            <Home className="w-5 h-5" />
            <span className="text-xs">Status</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default PousadaMobile;